// src/reports/StoreVelocityReport.jsx — Reporting → Store Reports → Store
// Velocity & Stats. Stock on hand, sales velocity and reorder suggestions per
// corporate store, from the Lightspeed POS sync (Eric, 2026-09-28). Modeled
// on InventoryDashboardReport.jsx (header, tiles, table, fetch pattern).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, BlockStack, InlineStack, Text, Button, ButtonGroup, TextField, Select, Banner, Badge, Spinner, EmptyState, Tooltip, Link } from '@shopify/polaris';
import { LineChart, TooltipContent } from '@shopify/polaris-viz';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch, useAuth } from '../AuthComponent';
import { isoDaysAgo, ReportPicker } from '../Reporting';
import { applySort } from './storeVelocityHelpers';

import { hasRole } from '../constants/roles';
const fmtN = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmt1 = n => (n === null || n === undefined || Number.isNaN(Number(n))) ? '—' : Number(n).toFixed(1);
function fmtAsOf(iso) {
    if (!iso) return 'not synced yet';
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
    return `${date} ${time} ET`;
}

function useDebounced(value, ms) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
    return v;
}

// House tile style (InventoryDashboardReport's `Tile`).
function Tile({ label, value, tone, help, sub }) {
    const bg = { ok: '#e3f3ea', info: '#e6edfb', warn: '#fdebd3', bad: '#fde2e1', muted: '#f1f2f4' }[tone || 'muted'];
    const body = (
        <div style={{ background: bg, borderRadius: 8, padding: '10px 14px', minWidth: 0, width: '100%', height: 112, boxSizing: 'border-box', overflow: 'hidden' }}>
            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
            <div style={{ fontSize: 12, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
            <div style={{ fontSize: 12, color: '#6d7175', marginTop: 4, height: 32, lineHeight: '16px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} title={sub || undefined}>{sub || '\u00a0'}</div>
        </div>
    );
    return help ? <Tooltip content={help} preferredPosition="above" dismissOnMouseOut><div style={{ display: 'flex', minWidth: 0 }}>{body}</div></Tooltip> : body;
}

const DAYS_OPTIONS = [7, 30, 90];
const STOCK_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'In stock', value: 'in_stock' },
    { label: 'Zero stock', value: 'zero' },
    { label: 'Sold in window', value: 'sold' },
];
const COLUMNS = [
    { key: 'sku', label: 'SKU', help: 'The Lightspeed custom SKU. Click it to filter the whole report to this item.' },
    { key: 'product', label: 'Product', help: 'The product (matrix) name; the item description is the second line when it differs.' },
    { key: 'size', label: 'Size', help: 'Size attribute from the Lightspeed item matrix.' },
    { key: 'brand', label: 'Brand', help: 'Manufacturer on the Lightspeed item.' },
    { key: 'category', label: 'Category', help: 'Last part of the Lightspeed category path; hover the value for the full path. The Category filter lists the top-level categories only and includes their sub-categories.' },
    { key: 'on_hand', label: 'On hand', right: true, help: 'Quantity on hand at this store in Lightspeed, as of the last inventory sync.' },
    { key: 'reorder_point', label: 'Reorder pt', right: true, help: 'Reorder point set on the item for this store in Lightspeed. Rows at or below it get the Reorder badge.' },
    { key: 'sold_7', label: 'Sold 7d', right: true, help: 'Units sold at this store in the last 7 days (POS sales, completed and not voided; returns count negative).' },
    { key: 'sold_30', label: 'Sold 30d', right: true, help: 'Units sold at this store in the last 30 days.' },
    { key: 'sold_90', label: 'Sold 90d', right: true, help: 'Units sold at this store in the last 90 days.' },
    { key: 'velocity_week', label: 'Velocity /wk', right: true, help: 'Average units sold per week, from the last 30 days: Sold 30d ÷ 30 × 7.' },
    { key: 'weeks_cover', label: 'Weeks cover', right: true, help: 'How many weeks the on-hand stock lasts at the current velocity: On hand ÷ Velocity. Blank when nothing sold in 30 days; red under 2 weeks.' },
    { key: 'incoming', label: 'Incoming (B2B)', right: true, help: 'Units of this SKU on open B2B orders placed with this store\'s B2B account (not yet fulfilled).' },
    { key: 'b2b_stock', label: 'B2B stock', right: true, help: 'Units available across the B2B warehouses right now (nightly B2B inventory snapshot).' },
    { key: 'suggested_qty', label: 'Suggested qty', right: true, help: 'Suggested order: Velocity × weeks of cover (2 for east-coast stores, 3 elsewhere; Settings → Lightspeed) − On hand − Incoming, never below 0 and never more than B2B stock. Blank when B2B is out.' },
    { key: 'price', label: 'Price', right: true, help: 'The item\'s default selling price in Lightspeed (its regular POS price, before any discount or price level).' },
];
const cell = { padding: '5px 10px', whiteSpace: 'nowrap' };
// The SKU and Product columns stay put while the rest of the table scrolls sideways.
// Widths are adjustable (drag the handle at the right edge of the SKU / Product
// header) and remembered per browser.
const COL_W_KEY = 'storeVelocity.colWidths';
const COL_W_DEFAULT = { sku: 230, product: 300 };
const COL_W_MIN = 120;
const COL_W_MAX = 640;
function loadColWidths() {
    try { const v = JSON.parse(localStorage.getItem(COL_W_KEY) || 'null'); if (v && v.sku && v.product) return { sku: Number(v.sku), product: Number(v.product) }; } catch (e) { /* ignore */ }
    return COL_W_DEFAULT;
}
function stickyStyles(w) {
    return {
        sku: { position: 'sticky', left: 0, zIndex: 2, background: '#fff', width: w.sku, minWidth: w.sku, maxWidth: w.sku, overflow: 'hidden', textOverflow: 'ellipsis' },
        product: { position: 'sticky', left: w.sku, zIndex: 2, background: '#fff', width: w.product, minWidth: w.product, maxWidth: w.product, overflow: 'hidden', textOverflow: 'ellipsis', boxShadow: '4px 0 6px -4px rgba(0,0,0,0.25)' },
    };
}
const RESIZER_STYLE = { position: 'absolute', top: 0, right: 0, width: 8, height: '100%', cursor: 'col-resize', userSelect: 'none', zIndex: 4 };
const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export default function StoreVelocityReport() {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    // 2026-09-28: no role-gated action lives on this page (the backend scopes
    // /locations and /store_velocity per caller independently) — isAdmin only
    // decides whether the raw Lightspeed shop mapping is worth showing.
    const isAdmin = hasRole(user, ['admin', 'finance', 'site_manager']);

    const [locations, setLocations] = useState([]);
    const [locLoaded, setLocLoaded] = useState(false);
    const [locErr, setLocErr] = useState(null);
    // Filters live in the URL so a view can be shared (?location_id=…&days=…&category=…&q=…&size=…&stock=…).
    const [searchParams, setSearchParams] = useSearchParams();
    const sp = (k, d = '') => searchParams.get(k) ?? d;
    const [locationId, setLocationId] = useState(sp('location_id'));
    const [days, setDays] = useState([7, 30, 90].includes(Number(sp('days', '30'))) ? Number(sp('days', '30')) : 30);
    const [category, setCategory] = useState(sp('category'));
    const [q, setQ] = useState(sp('q'));
    const [size, setSize] = useState(sp('size'));
    const [stock, setStock] = useState(sp('stock', 'all'));
    useEffect(() => {
        const n = new URLSearchParams();
        if (locationId) n.set('location_id', locationId);
        if (days !== 30) n.set('days', String(days));
        if (category) n.set('category', category);
        if (q) n.set('q', q);
        if (size) n.set('size', size);
        if (stock && stock !== 'all') n.set('stock', stock);
        if (n.toString() !== searchParams.toString()) setSearchParams(n, { replace: true });
    }, [locationId, days, category, q, size, stock]); // eslint-disable-line react-hooks/exhaustive-deps
    // Biggest suggested order first by default (Eric, 2026-10-05).
    const [sort, setSort] = useState({ key: 'suggested_qty', dir: 'desc' });
    const [chartHidden, setChartHidden] = useState(false);
    const [tilesHidden, setTilesHidden] = useState(false);
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const qDebounced = useDebounced(q, 400);


    // Corporate stores in the caller's scope that have a Lightspeed shop mapped.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchAuth('/api/reports/store_velocity/locations');
                const j = await r.json();
                if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
                if (cancelled) return;
                const locs = j.locations || [];
                setLocations(locs);
                if (locs.length === 1) setLocationId(String(locs[0].id));
            } catch (e) { if (!cancelled) setLocErr(e.message); }
            finally { if (!cancelled) setLocLoaded(true); }
        })();
        return () => { cancelled = true; };
    }, [fetchAuth]);

    const load = useCallback(async () => {
        if (!locationId) { setReport(null); return; }
        setBusy(true); setErr(null);
        try {
            const params = new URLSearchParams({ location_id: locationId, days: String(days), stock, page: '1', page_size: '1000' });
            if (category) params.set('category', category);
            if (qDebounced) params.set('q', qDebounced);
            if (size) params.set('size', size);
            const r = await fetchAuth(`/api/reports/store_velocity?${params}`);
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setReport(j);
        } catch (e) { setErr(e.message); setReport(null); }
        finally { setBusy(false); }
    }, [fetchAuth, locationId, days, category, qDebounced, size, stock]);

    // Store + days + every filter refetch on change — Run Report is the
    // initial trigger and a manual refresh, not a gate.
    useEffect(() => { load(); }, [load]);

    const locationOptions = useMemo(() => locations.map(l => ({ label: l.name, value: String(l.id) })), [locations]);
    const categoryOptions = useMemo(() => [{ label: 'All categories', value: '' },
        ...((report?.facets?.categories || []).map(c => ({ label: c, value: c })))], [report]);
    const sizeOptions = useMemo(() => [{ label: 'All sizes', value: '' },
        ...((report?.facets?.sizes || []).map(s => ({ label: s, value: s })))], [report]);

    // Client-side re-narrows nothing the server hasn't already filtered on `q`
    // (`filterRows` is the pure word-by-word helper used for its own tests) —
    // the visible rows are simply the server page, sorted.
    const rows = useMemo(() => report?.rows || [], [report]);
    const sortedRows = useMemo(() => applySort(rows, sort.key, sort.dir), [rows, sort]);
    const toggleSort = useCallback((key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })), []);
    const [colW, setColW] = useState(loadColWidths);
    const STICKY = useMemo(() => stickyStyles(colW), [colW]);
    const startResize = useCallback((key, e) => {
        e.preventDefault(); e.stopPropagation();
        const startX = e.clientX; const startW = colW[key];
        const onMove = (ev) => setColW(w => ({ ...w, [key]: Math.max(COL_W_MIN, Math.min(COL_W_MAX, startW + ev.clientX - startX)) }));
        const onUp = () => {
            window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp);
            setColW(w => { try { localStorage.setItem(COL_W_KEY, JSON.stringify(w)); } catch (err) { /* ignore */ } return w; });
        };
        window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    }, [colW]);

    // Chart: every day of the window, in the browser's local date, 0-filled
    // where the API returned no point for that day.
    const chartDays = useMemo(() => {
        const byDate = new Map((report?.series || []).map(s => [s.date, s]));
        const out = [];
        for (let i = days - 1; i >= 0; i -= 1) {
            const key = isoDaysAgo(i);
            const hit = byDate.get(key);
            out.push({ key, units: hit ? hit.units : 0, sales: hit ? hit.sales : 0 });
        }
        return out;
    }, [report, days]);
    const chartData = useMemo(() => ([{ name: 'Units sold', data: chartDays.map(d => ({ key: d.key, value: d.units })) }]), [chartDays]);
    const renderTooltipContent = useCallback((data) => {
        const day = chartDays[data.activeIndex];
        if (!day) return null;
        return (
            <TooltipContent title={day.key} data={[{
                shape: 'Line',
                data: [
                    { key: 'Units sold', value: fmtN(day.units) },
                    { key: 'Sales', value: fmtUSD(day.sales) },
                ],
            }]} />
        );
    }, [chartDays]);

    if (locLoaded && !locations.length) {
        return (
            <Card>
                <EmptyState heading="No Lightspeed store is mapped to your account" image="">
                    <p>Ask an admin to map it under Settings → Lightspeed.</p>
                </EmptyState>
            </Card>
        );
    }

    const store = report?.store;
    const summary = report?.summary;

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="200">
                    <Text as="h2" variant="headingMd">Store Ordering</Text>
                    <Text as="p" tone="subdued">
                        What each corporate store should order from B2B, from stock on hand and sales velocity in the Lightspeed POS.
                        Suggestions cover a 2-week supply at east-coast stores and 3 weeks everywhere else, and never more than B2B has.
                    </Text>
                    {locErr && <Banner tone="critical" onDismiss={() => setLocErr(null)}>{locErr}</Banner>}
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    <InlineStack gap="300" blockAlign="center" wrap>
                        <ReportPicker label="Store" options={locationOptions} value={locationId} onChange={setLocationId} />
                        <ButtonGroup variant="segmented">
                            {DAYS_OPTIONS.map(d => (
                                <Button key={d} pressed={days === d} onClick={() => setDays(d)}>{d} days</Button>
                            ))}
                        </ButtonGroup>
                        <Button variant="primary" onClick={load} loading={busy} disabled={!locationId}>Run Report</Button>
                    </InlineStack>
                    {report && (
                        <Text as="span" tone="subdued">
                            Inventory as of {fmtAsOf(report.as_of?.inventory)} · Sales as of {fmtAsOf(report.as_of?.sales)}
                            {isAdmin && store ? ` · Lightspeed shop: ${store.shop_name} (#${store.shop_id})` : ''}
                        </Text>
                    )}
                </BlockStack>
            </Card>

            {!locationId && (
                <Card><Text as="p" tone="subdued">Pick a store to load its stock and sales velocity.</Text></Card>
            )}

            {locationId && (
                <>
                    <Card>
                        <InlineStack gap="300" blockAlign="end" wrap>
                            <ReportPicker label="Category" options={categoryOptions} value={category} onChange={setCategory} searchable />
                            <div style={{ minWidth: 220 }}>
                                <TextField label="SKU / product" labelHidden placeholder="Search SKU or product…"
                                    value={q} onChange={setQ} autoComplete="off" clearButton onClearButtonClick={() => setQ('')} />
                            </div>
                            <ReportPicker label="Size" options={sizeOptions} value={size} onChange={setSize} searchable />
                            <div style={{ minWidth: 220 }}>
                                <Select label="Stock" options={STOCK_OPTIONS} value={stock} onChange={setStock} />
                            </div>
                        </InlineStack>
                    </Card>

                    {busy && !report && (
                        <Card><div style={{ textAlign: 'center', padding: 40 }}><Spinner accessibilityLabel="Loading Store Ordering" size="large" /></div></Card>
                    )}

                    {report && summary && (
                        <>
                            <Card>
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h3" variant="headingSm">Stock and sales — {report.store?.name}</Text>
                                    <Button variant="plain" onClick={() => setTilesHidden(h => !h)}>{tilesHidden ? 'Show tiles' : 'Hide tiles'}</Button>
                                </InlineStack>
                                {!tilesHidden && (<>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, marginTop: 8 }}>
                                    <Tile label="SKUs in stock" value={fmtN(summary.skus_in_stock)} tone="ok"
                                        help="Distinct SKUs with an on-hand quantity above 0 at this store, after the filters." />
                                    <Tile label="Units on hand" value={fmtN(summary.units_on_hand)} tone="info"
                                        help="Sum of the on-hand quantities of those SKUs (a negative quantity counts as 0)." />
                                    <Tile label={`Units sold (${days}d)`} value={fmtN(summary.units_sold)} tone="info"
                                        help={`Units sold at this store in the last ${days} days on completed, non-voided POS sales, after the filters. Returns count negative.`} />
                                    <Tile label="Incoming from B2B" value={fmtN(summary.units_incoming)} tone="info"
                                        help="Units on open (not yet fulfilled) B2B orders placed with this store's B2B account, for the SKUs in view." />
                                    <Tile label="Sold out" value={fmtN(summary.skus_sold_out)} tone="bad"
                                        help="SKUs with nothing on hand that still sold in the last 30 days." />
                                </div>
                                {summary.pos && (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ fontSize: 12, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '.04em', margin: '6px 2px' }}>Sales, last {days} days — the whole store (the filters above do not apply to this row)</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8 }}>
                                            <Tile label="Gross sales" value={fmtUSD(summary.pos.gross)} tone="ok"
                                                help="Everything sold at list price before discounts: the sum of sold lines (quantity above 0) at their line subtotal, tax excluded." />
                                            <Tile label="Returns" value={`−${fmtUSD(summary.pos.returns)}`} tone="bad"
                                                sub={`${fmtN(summary.pos.units_returned)} units · ${fmtN(summary.pos.refund_transactions)} refund transactions · ${summary.pos.gross ? (100 * summary.pos.returns / summary.pos.gross).toFixed(1) : '0.0'}% of gross`}
                                                help="Merchandise taken back: the sum of returned lines (quantity below 0) at their line subtotal, tax excluded." />
                                            <Tile label="Discounts" value={`−${fmtUSD(summary.pos.discounts)}`} tone="warn"
                                                sub={`${fmtN(summary.pos.discounted_transactions)} of ${fmtN(summary.pos.transactions)} transactions (${summary.pos.transactions ? Math.round(100 * summary.pos.discounted_transactions / summary.pos.transactions) : 0}%) · ${summary.pos.gross ? (100 * summary.pos.discounts / summary.pos.gross).toFixed(1) : '0.0'}% of gross`}
                                                help="Line and transaction discounts given (member discounts, promo codes, manager discounts). A discount reduces the sale; it is not a tender." />
                                            <Tile label="Net sales" value={fmtUSD(summary.pos.net_sales)} tone="info"
                                                help="Gross sales − Returns − Discounts. The merchandise revenue figure, tax excluded." />
                                            <Tile label="Tax" value={fmtUSD(summary.pos.tax)} tone="muted"
                                                help="Sales tax collected on those sales." />
                                            <Tile label="Total collected" value={fmtUSD(summary.pos.total)} tone="ok" sub={`${fmtN(summary.pos.transactions)} transactions`}
                                                help="Net sales + Tax: the money taken at the registers, returns and refunds netted. This is the figure Lightspeed and the old Grafana dashboard report." />
                                            <Tile label="Gift cards redeemed" value={fmtUSD(summary.pos.gift_card_redeemed)} tone="muted" sub={`sold: ${fmtUSD(summary.pos.gift_cards_sold)}${summary.pos.gift_cards_sold_count ? ` (${fmtN(summary.pos.gift_cards_sold_count)} cards)` : ''}`}
                                                help="Gift cards used as payment on these sales. A gift card is a tender, not a discount, so it is inside Total collected. 'Sold' is the value loaded onto cards at this store (a card bought on its own rings up as a $0 sale with the card as a negative tender), which is not merchandise revenue." />
                                        </div>
                                    </div>
                                )}
                                </>)}
                            </Card>

                            <Card>
                                <BlockStack gap="200">
                                    <InlineStack align="space-between" blockAlign="center">
                                        <Text as="h3" variant="headingSm">Units sold — last {days} days</Text>
                                        <Button variant="plain" onClick={() => setChartHidden(h => !h)}>{chartHidden ? 'Show chart' : 'Hide chart'}</Button>
                                    </InlineStack>
                                    {!chartHidden && (
                                        <div style={{ height: 260 }}>
                                            <LineChart data={chartData} tooltipOptions={{ renderTooltipContent }} />
                                        </div>
                                    )}
                                </BlockStack>
                            </Card>

                                    <Card padding="0">
                                        <div style={{ padding: 12, borderBottom: '1px solid #e1e3e5' }}>
                                            <InlineStack align="space-between" blockAlign="center" wrap>
                                                <Text as="span" tone="subdued"><b>{fmtN(sortedRows.length)}</b> SKUs{busy ? ' · loading…' : ''}</Text>
                                                {report.total > 1000 && (
                                                    <Text as="span" tone="subdued">Showing the first 1,000 of {fmtN(report.total)} rows — narrow the filters</Text>
                                                )}
                                            </InlineStack>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <div style={{ overflow: 'auto', maxHeight: '70vh' }}>
                                                <table className="report-table" style={{ minWidth: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap', fontSize: 13 }}>
                                                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                                        <tr>
                                                            {COLUMNS.map(c => (
                                                                <th key={c.key} onClick={() => toggleSort(c.key)}
                                                                    style={{ ...cell, ...(STICKY[c.key] || {}), ...(STICKY[c.key] ? { zIndex: 3 } : {}), cursor: 'pointer', textAlign: c.right ? 'right' : 'left', borderBottom: '1px solid #e1e3e5' }}>
                                                                    {STICKY[c.key] && (
                                                                        <span role="separator" aria-orientation="vertical" title="Drag to resize"
                                                                            style={RESIZER_STYLE} onMouseDown={e => startResize(c.key, e)} onClick={e => e.stopPropagation()} />
                                                                    )}
                                                                    <Tooltip content={c.help} preferredPosition="above" dismissOnMouseOut>
                                                                        <span style={{ borderBottom: '1px dotted #8c9196' }}>{c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</span>
                                                                    </Tooltip>
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sortedRows.map(r => {
                                                            const atReorder = r.reorder_point > 0 && r.on_hand <= r.reorder_point;
                                                            const soldOut = r.on_hand <= 0 && r.sold_30 > 0;
                                                            const lowCover = r.weeks_cover !== null && r.weeks_cover < 2;
                                                            return (
                                                                <tr key={r.item_id} style={{ borderBottom: '1px solid #f1f2f3' }}>
                                                                    <td style={{ ...cell, ...STICKY.sku, fontFamily: 'monospace', fontSize: 12 }}>
                                                                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                                                                            {r.sku ? (
                                                                                <button type="button" onClick={() => setQ(r.sku)} title={`Filter the report to ${r.sku}`}
                                                                                    style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: '#005bd3', cursor: 'pointer', textDecoration: 'underline' }}>
                                                                                    {r.sku}
                                                                                </button>
                                                                            ) : <span>—</span>}
                                                                            {soldOut && <Badge tone="critical">Sold out</Badge>}
                                                                            {atReorder && <Badge tone="warning">Reorder</Badge>}
                                                                        </InlineStack>
                                                                    </td>
                                                                    <td style={{ ...cell, ...STICKY.product }} title={r.product || ''}>
                                                                        <BlockStack gap="0">
                                                                            {/* Links to the product on B2B when B2B lists it, as the Store Briefing does (Eric, 2026-10-05). */}
                                                                            {r.b2b_handle
                                                                                ? <Link url={`https://soccerpostb2b.com/products/${encodeURIComponent(r.b2b_handle)}`} target="_blank" removeUnderline>{r.product || '—'}</Link>
                                                                                : <Text as="span">{r.product || '—'}</Text>}
                                                                            {r.description && r.product && !r.description.startsWith(r.product) && !r.product.startsWith(r.description) && (
                                                                                <Text as="span" tone="subdued" variant="bodySm">{r.description}</Text>
                                                                            )}
                                                                        </BlockStack>
                                                                    </td>
                                                                    <td style={cell}>{r.size || '—'}</td>
                                                                    <td style={cell}>{r.brand || '—'}</td>
                                                                    <td style={cell} title={r.category || ''}>{(r.category || '—').split('/').pop()}</td>
                                                                    <td style={num}>{fmtN(r.on_hand)}</td>
                                                                    <td style={num}>{fmtN(r.reorder_point)}</td>
                                                                    <td style={num}>{fmtN(r.sold_7)}</td>
                                                                    <td style={num}>{fmtN(r.sold_30)}</td>
                                                                    <td style={num}>{fmtN(r.sold_90)}</td>
                                                                    <td style={num}>{fmt1(r.velocity_week)}</td>
                                                                    <td style={num}><Text as="span" tone={lowCover ? 'critical' : undefined}>{r.weeks_cover === null ? '—' : fmt1(r.weeks_cover)}</Text></td>
                                                                    <td style={num}>{fmtN(r.incoming)}</td>
                                                                    <td style={num}>{fmtN(r.b2b_stock)}</td>
                                                                    <td style={{ ...num, fontWeight: r.suggested_qty > 0 ? 700 : 400 }}>{fmtN(r.suggested_qty)}</td>
                                                                    <td style={num}>{fmtUSD(r.price)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {!busy && sortedRows.length === 0 && (
                                                            <tr><td colSpan={COLUMNS.length + 1} style={{ padding: 24, textAlign: 'center', color: '#6d7175' }}>No SKUs match these filters.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </Card>
                        </>
                    )}
                </>
            )}

        </BlockStack>
    );
}
